/**
 * fab-portrait-controller.js
 *
 * Personalised PDP (product-authored fields). Runs instead of
 * fab-custom-print-controller.js when main-product-custom-print resolves
 * portrait / personalised mode. Owns:
 *
 *   - per-slot photo pick → decode (HEIC via native or lazy heic2any) →
 *     uncropped JPEG thumbnail (≤1200px, ≤2 MiB) → upload-custom-print
 *     (X-Fab-Mode: personalised on the universal path) → poll → receipt
 *   - GET/POST prepare-personalisation (config_hash + cart properties)
 *   - product-authored inputs, or the hardcoded Dogue name/background form
 *   - per-product configuration storage (separate from FabCpLibrary)
 *   - add-to-cart gate on <product-info> capture-phase submit (main + sticky)
 *
 * Pure logic lives in fab-portrait-core.js.
 */
(function () {
  'use strict';
  if (window.FabPortraitController) return;

  var Core = window.FabPortraitCore;
  if (!Core) {
    console.error('[fab-portrait] FabPortraitCore missing — load fab-portrait-core.js first');
    return;
  }

  var PERSIST_DEBOUNCE_MS = 250;
  var JPEG_QUALITIES = [0.88, 0.8, 0.7, 0.6];
  var EDGE_FALLBACKS = [1200, 1000, 800];

  function $(root, sel) { return root ? root.querySelector(sel) : null; }
  function $$(root, sel) { return root ? Array.prototype.slice.call(root.querySelectorAll(sel)) : []; }

  function readJsonScript(root, sel) {
    var el = $(root, sel);
    if (!el) return {};
    try { return JSON.parse(el.textContent.trim()) || {}; } catch (_) { return {}; }
  }

  function readJsonArray(root, sel) {
    var el = $(root, sel);
    if (!el) return [];
    try {
      var parsed = JSON.parse(el.textContent.trim());
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) { return []; }
  }

  function decodeViaImageElement(blob) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(blob);
      var decoder = document.createElement('img');
      decoder.decoding = 'async';
      decoder.addEventListener('load', function () {
        resolve({
          source: decoder,
          width: decoder.naturalWidth,
          height: decoder.naturalHeight,
          revoke: function () { URL.revokeObjectURL(url); },
        });
      }, { once: true });
      decoder.addEventListener('error', function () {
        URL.revokeObjectURL(url);
        reject(new Error('decode_failed'));
      }, { once: true });
      decoder.src = url;
    });
  }

  async function decodeNative(blob) {
    if (typeof createImageBitmap === 'function') {
      try {
        var bmp = await createImageBitmap(blob);
        if (bmp && bmp.width > 0 && bmp.height > 0) {
          return { source: bmp, width: bmp.width, height: bmp.height, revoke: function () { if (bmp.close) bmp.close(); } };
        }
      } catch (_) { /* fall through to the element decoder */ }
    }
    return decodeViaImageElement(blob);
  }

  var heicLoader = null;
  function loadHeicDecoder(url) {
    if (window.heic2any) return Promise.resolve(window.heic2any);
    if (!url) return Promise.reject(new Error('heic_decoder_unavailable'));
    if (heicLoader) return heicLoader;
    heicLoader = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = url;
      s.async = true;
      s.onload = function () { window.heic2any ? resolve(window.heic2any) : reject(new Error('heic_decoder_unavailable')); };
      s.onerror = function () { heicLoader = null; reject(new Error('heic_decoder_unavailable')); };
      document.head.appendChild(s);
    });
    return heicLoader;
  }

  async function decodeFile(file, mime, heicDecoderUrl) {
    try {
      return await decodeNative(file);
    } catch (nativeErr) {
      if (!Core.isHeicMime(mime)) throw nativeErr;
    }
    var heic2any = await loadHeicDecoder(heicDecoderUrl);
    var converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    var jpeg = Array.isArray(converted) ? converted[0] : converted;
    if (!jpeg) throw new Error('decode_failed');
    return decodeNative(jpeg);
  }

  function canvasToBlob(canvas, quality) {
    return new Promise(function (resolve) {
      canvas.toBlob(function (b) { resolve(b); }, 'image/jpeg', quality);
    });
  }

  async function buildPreview(decoded) {
    for (var e = 0; e < EDGE_FALLBACKS.length; e++) {
      var size = Core.computePreviewSize(decoded.width, decoded.height, EDGE_FALLBACKS[e]);
      var canvas = document.createElement('canvas');
      canvas.width = size.width;
      canvas.height = size.height;
      var ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size.width, size.height);
      ctx.drawImage(decoded.source, 0, 0, size.width, size.height);
      for (var q = 0; q < JPEG_QUALITIES.length; q++) {
        var blob = await canvasToBlob(canvas, JPEG_QUALITIES[q]);
        if (!blob) throw new Error('preview_failed');
        if (blob.size <= Core.MAX_PREVIEW_BYTES) {
          return { blob: blob, width: size.width, height: size.height };
        }
      }
    }
    throw new Error('preview_failed');
  }

  function collectSlotUi(el) {
    return {
      root: el,
      fileInput: $(el, '[data-fab-portrait-file]'),
      uploadRow: $(el, '[data-fab-portrait-upload]'),
      uploadBtn: $(el, '[data-fab-portrait-upload-btn]'),
      progress: $(el, '[data-fab-portrait-progress]'),
      progressLabel: $(el, '[data-fab-portrait-progress-label]'),
      progressFill: $(el, '[data-fab-portrait-progress-fill]'),
      preview: $(el, '[data-fab-portrait-preview]'),
      previewImg: $(el, '[data-fab-portrait-preview-img]'),
      status: $(el, '[data-fab-portrait-status]'),
      changeBtn: $(el, '[data-fab-portrait-change]'),
      removeBtn: $(el, '[data-fab-portrait-remove]'),
      error: $(el, '[data-fab-portrait-error]'),
    };
  }

  /* ------------------------------------------------------------------ */
  /* Controller                                                          */
  /* ------------------------------------------------------------------ */

  function Controller(sectionEl) {
    this.section = sectionEl;
    this.root = sectionEl.closest('product-info') || sectionEl;
    this.el = $(sectionEl, '[data-fab-portrait]');
    if (!this.el) return;

    var d = this.el.dataset;
    this.productId = d.productId || '';
    this.styleKey = d.styleKey || 'dogue';
    this.styleVersion = d.styleVersion || '1';
    this.outputOrientation = d.outputOrientation || 'portrait';
    this.formId = d.formId || '';
    this.heicDecoderUrl = d.heicDecoderUrl || '';
    this.universal = this.el.hasAttribute('data-fab-universal');
    this.i18n = readJsonScript(this.el, 'script[data-fab-portrait-i18n]');
    this.fields = this.universal ? readJsonArray(this.el, 'script[data-fab-portrait-schema]') : [];
    this.imageFields = this.fields.filter(function (f) { return f && f.key && String(f.type).toLowerCase() === 'image'; });
    this.valueFields = this.fields.filter(function (f) { return f && f.key && String(f.type).toLowerCase() !== 'image'; });

    this.slots = {};
    this.slotTokens = {};
    this.localThumbs = {};
    var self = this;
    $$(this.el, '[data-fab-portrait-slot]').forEach(function (slotEl) {
      var key = slotEl.getAttribute('data-fab-portrait-slot');
      if (!key) return;
      self.slots[key] = collectSlotUi(slotEl);
      self.slotTokens[key] = 0;
    });

    this.ui = {
      formError: $(this.el, '[data-fab-portrait-form-error]'),
      propsHost: $(this.el, '[data-fab-portrait-props]'),
      props: $$(this.el, '[data-fab-portrait-prop]'),
      name: $(this.el, '[data-fab-portrait-name]'),
      bgRadios: $$(this.el, '[data-fab-portrait-bg]'),
      otherWrap: $(this.el, '[data-fab-portrait-other-wrap]'),
      other: $(this.el, '[data-fab-portrait-other]'),
    };

    this.state = {
      coverTitle: '',
      backgroundChoice: 'director',
      backgroundOther: '',
      values: {},
      uploads: {},
      upload: null,
      configHash: '',
      customisationId: '',
      fingerprint: '',
    };
    this.configReady = !this.universal;
    this.busy = false;
    this.progressSlot = '';
    this.passSubmit = false;
    this.preparing = false;
    this.persistTimer = null;
    this.previewEnabled = false;
    this.previewQuota = null;
    this._quotaAt = 0;
    this._quotaTimer = 0;
    this.printPreview = null;
    this.printPreviewUnmount = null;
    this._previewMountedSession = '';
    this._previewVariantId = '';
    this._previewPending = null;
    this._previewStarting = false;
    this._previewArtworkTimer = 0;
    this._previewActivated = false;
    this._previewFrozenPayload = null;
    this._previewCartFrozen = false;
    this._previewFrameOnly = false;
    this._previewPendingFrame = false;
    this._previewLastKey = '';
    this._previewRoomReady = false;
    this._previewCloseReady = false;
    this._previewDecoding = false;
    this._previewMotionStarted = false;
    this._previewResume = false;
    this._previewRevealEpoch = 0;
    this._previewRevealedId = '';
    this._previewCloseFailed = false;
    this._previewRoomFailed = false;
    this._previewRevealing = false;

    var cfg = window.fab || {};
    try {
      this.uploader = new window.FabCpUploader({
        uploadEndpoint: cfg.uploadEndpoint,
        statusEndpoint: cfg.statusEndpoint,
        supabaseAnonJwt: cfg.supabaseAnonJwt,
        onProgress: function (p) { self.onProgress(p); },
      });
    } catch (err) {
      console.error('[fab-portrait] uploader config missing', err);
      this.uploader = null;
    }

    this.bind();
    this.restore();
  }

  Controller.prototype.t = function (key, fallback) {
    return (this.i18n && this.i18n[key]) || fallback || key;
  };

  Controller.prototype.authHeaders = function () {
    var jwt = (window.fab && window.fab.supabaseAnonJwt) || '';
    var token = (this.uploader && this.uploader.sessionToken)
      || (window.FabCpUploader && window.FabCpUploader.getOrCreateSessionToken && window.FabCpUploader.getOrCreateSessionToken())
      || '';
    return {
      'Authorization': 'Bearer ' + jwt,
      'apikey': jwt,
      'X-Session-Token': token,
      'Accept': 'application/json',
    };
  };

  Controller.prototype.formEl = function () {
    return this.formId ? document.getElementById(this.formId) : null;
  };

  Controller.prototype.selectedVariantId = function () {
    var form = this.formEl();
    var input = form && form.querySelector('[name="id"]');
    if (input && input.value) return String(input.value);
    var fallback = this.root.querySelector('.product-variant-id, [name="id"]');
    return fallback && fallback.value ? String(fallback.value) : '';
  };

  /* ---------------- persistence ---------------- */

  Controller.prototype.storageKey = function () {
    return this.universal ? Core.storageKeyV2(this.productId) : Core.storageKey(this.productId);
  };

  Controller.prototype.persist = function () {
    var self = this;
    clearTimeout(this.persistTimer);
    this.persistTimer = setTimeout(function () {
      try {
        var s = self.state;
        var uploads = {};
        Object.keys(s.uploads || {}).forEach(function (key) {
          var rec = Core.sanitiseUploadRecord(s.uploads[key]);
          if (rec && rec.uploadId) uploads[key] = rec;
        });
        var payload;
        if (self.universal) {
          payload = {
            v: 2,
            configHash: s.configHash || '',
            customisationId: s.customisationId,
            fingerprint: s.fingerprint,
            values: s.values || {},
            uploads: uploads,
            savedAt: Date.now(),
          };
        } else {
          var u = s.upload || s.uploads.photo;
          payload = {
          v: 1,
          coverTitle: s.coverTitle,
          backgroundChoice: s.backgroundChoice,
          backgroundOther: s.backgroundOther,
          customisationId: s.customisationId,
          fingerprint: s.fingerprint,
            upload: u ? Core.sanitiseUploadRecord(u) : null,
          savedAt: Date.now(),
        };
        }
        localStorage.setItem(self.storageKey(), JSON.stringify(payload));
      } catch (_) { /* storage unavailable — session-only */ }
    }, PERSIST_DEBOUNCE_MS);
  };

  Controller.prototype.readStored = function () {
    try {
      var raw = localStorage.getItem(this.storageKey());
      if (!raw && this.universal) raw = localStorage.getItem(Core.storageKey(this.productId));
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (_) { return null; }
  };

  Controller.prototype.restore = async function () {
    var stored = this.readStored();
    if (stored) {
      if (this.universal) {
        if (stored.v === 2) {
          this.state.values = stored.values && typeof stored.values === 'object' ? stored.values : {};
          this.state.configHash = stored.configHash || '';
          this.state.customisationId = stored.customisationId || '';
          this.state.fingerprint = stored.fingerprint || '';
          Object.keys(stored.uploads || {}).forEach(function (key) {
            var rec = stored.uploads[key];
            if (rec && Core.isUuid(rec.uploadId)) {
              this.state.uploads[key] = Object.assign(Core.sanitiseUploadRecord(rec), { verified: false });
            }
          }, this);
        } else if (stored.v === 1) {
          this.state.values = {};
          if (typeof stored.coverTitle === 'string') this.state.values.name = stored.coverTitle;
          if (stored.backgroundChoice) {
            var bg = Core.resolveBackground(stored.backgroundChoice, stored.backgroundOther);
            this.state.values.background = bg.isDefault ? '' : bg.value;
          }
          this.state.customisationId = stored.customisationId || '';
          if (stored.upload && Core.isUuid(stored.upload.uploadId)) {
            this.state.uploads.photo = Object.assign(Core.sanitiseUploadRecord(stored.upload), { verified: false });
          }
        }
      } else if (stored.v === 1) {
      this.state.coverTitle = typeof stored.coverTitle === 'string' ? stored.coverTitle : '';
      this.state.backgroundChoice = stored.backgroundChoice || 'director';
      this.state.backgroundOther = typeof stored.backgroundOther === 'string' ? stored.backgroundOther : '';
      this.state.customisationId = stored.customisationId || '';
      this.state.fingerprint = stored.fingerprint || '';
      if (stored.upload && Core.isUuid(stored.upload.uploadId)) {
          this.state.upload = Object.assign(Core.sanitiseUploadRecord(stored.upload), { verified: false });
          this.state.uploads.photo = this.state.upload;
        }
      }
    }
    this.applyStateToInputs();

    Object.keys(this.state.uploads).forEach(function (key) {
      var upload = this.state.uploads[key];
      if (!upload) return;
      this.showPreview(key, upload.previewUrl, 'checking', this.t('photo_status_unverified'));
    }, this);

    if (!this.universal) {
    if (this.state.upload) {
        await this.verifyRestoredUpload('photo');
    } else {
        this.showUploadRow('photo');
    }
    this.syncProps();
      return;
    }

    await this.loadRemoteConfig();
    var keys = Object.keys(this.state.uploads);
    for (var i = 0; i < keys.length; i++) {
      await this.verifyRestoredUpload(keys[i]);
    }
    this.syncProps();
    this.syncPrintPreview();
  };

  Controller.prototype.loadRemoteConfig = async function () {
    if (!this.universal) {
      this.configReady = true;
      return true;
    }
    var endpoint = (window.fab && window.fab.prepareEndpoint) || '';
    if (!endpoint || !this.productId) {
      this.configReady = false;
      this.showFormError(this.t('error_config_unavailable'));
      return false;
    }
    try {
      var res = await fetch(endpoint + '?product_id=' + encodeURIComponent(this.productId), {
        method: 'GET',
        headers: this.authHeaders(),
        cache: 'no-store',
      });
      var body = null;
      try { body = await res.json(); } catch (_) { body = null; }
      if (!res.ok || !body || !body.config_hash) {
        this.configReady = false;
        this.showFormError(this.t('error_config_unavailable'));
        return false;
      }
      var remoteFields = Array.isArray(body.fields) ? body.fields : [];
      if (this.fields.length && !Core.schemasMatch(this.fields, remoteFields)) {
        this.configReady = false;
        this.state.configHash = body.config_hash;
        this.showFormError(this.t('error_config_unavailable'));
        return false;
      }
      var prevHash = this.state.configHash;
      this.state.configHash = body.config_hash;
      this.configReady = true;
      this.previewEnabled = !!body.preview_enabled;
      this.applyPreviewQuota(body.preview_quota, Date.now());
      this.renderPreviewOffer();
      if (this.previewEnabled) this.fetchPreviewQuota();
      this.clearFormError();
      if (prevHash && prevHash !== body.config_hash) {
        this.clearAllUploads();
        this.showFormError(this.t('error_configuration_changed'));
      }
      this.syncProps();
      this.persist();
      this.syncPrintPreview();
      return true;
    } catch (_) {
      this.configReady = false;
      this.showFormError(this.t('error_config_unavailable'));
      return false;
    }
  };

  Controller.prototype.verifyRestoredUpload = async function (slotKey) {
    slotKey = slotKey || 'photo';
    if (!this.uploader) return;
    var upload = this.state.uploads[slotKey] || (slotKey === 'photo' ? this.state.upload : null);
    if (!upload) return;
    var token = ++this.slotTokens[slotKey];
    var result = await this.uploader.fetchStatus(upload.uploadId);
    if (token !== this.slotTokens[slotKey]) return;
    var decision = window.FabCpUploader.decideStatus(result);

    if (decision === 'approved') {
      var merged = Core.sanitiseUploadRecord(Core.mergeUploadResponse(upload, result.body));
      merged.verified = Core.slotIsReady(Object.assign({}, merged, { verified: true }));
      if (!merged.verified || !merged.receipt) {
        this.clearUpload(slotKey);
        this.showSlotError(slotKey, this.t('error_expired'));
        return;
      }
      merged.verified = true;
      this.state.uploads[slotKey] = merged;
      if (slotKey === 'photo') this.state.upload = merged;
      this.afterApproved(slotKey);
      return;
    }
    if (decision === 'rejected') {
      this.clearUpload(slotKey);
      this.showSlotError(slotKey, this.t('error_rejected'));
      return;
    }
    if (decision === 'expired' || decision === 'missing') {
      this.clearUpload(slotKey);
      this.showSlotError(slotKey, this.t('error_expired'));
      return;
    }
    upload.verified = false;
    this.setStatus(slotKey, 'checking', this.t('photo_status_unverified'));
    this.syncStickyThumb();
  };

  /* ---------------- UI state ---------------- */

  Controller.prototype.slotUi = function (slotKey) {
    return this.slots[slotKey] || this.slots.photo || null;
  };

  Controller.prototype.showUploadRow = function (slotKey) {
    var ui = this.slotUi(slotKey);
    if (!ui) return;
    if (ui.uploadRow) ui.uploadRow.hidden = false;
    if (ui.preview) ui.preview.hidden = true;
    this.hideProgress(slotKey);
  };

  Controller.prototype.showPreview = function (slotKey, src, statusState, statusText) {
    var ui = this.slotUi(slotKey);
    if (!ui) return;
    if (ui.uploadRow) ui.uploadRow.hidden = true;
    if (ui.previewImg && src) ui.previewImg.src = src;
    if (ui.preview) ui.preview.hidden = false;
    this.setStatus(slotKey, statusState, statusText);
  };

  Controller.prototype.setStatus = function (slotKey, state, text) {
    var ui = this.slotUi(slotKey);
    if (!ui || !ui.status) return;
    ui.status.dataset.state = state || '';
    ui.status.textContent = text || '';
  };

  Controller.prototype.showProgress = function (slotKey, label, pct) {
    var ui = this.slotUi(slotKey);
    if (!ui) return;
    if (ui.progress) ui.progress.hidden = false;
    if (ui.progressLabel && label) ui.progressLabel.textContent = label;
    if (ui.progressFill && typeof pct === 'number') {
      ui.progressFill.style.width = Math.max(0, Math.min(100, pct)) + '%';
    }
  };

  Controller.prototype.hideProgress = function (slotKey) {
    var ui = this.slotUi(slotKey);
    if (!ui) return;
    if (ui.progress) ui.progress.hidden = true;
    if (ui.progressFill) ui.progressFill.style.width = '0%';
  };

  Controller.prototype.onProgress = function (p) {
    if (!p) return;
    var slotKey = this.progressSlot || Object.keys(this.slots)[0] || 'photo';
    var label;
    if (p.phase === 'hashing') label = this.t('progress_preparing');
    else if (p.phase === 'uploading') label = this.t('progress_uploading');
    else if (p.phase === 'screening') label = this.t('progress_checking');
    if (p.phase === 'done') { this.hideProgress(slotKey); return; }
    this.showProgress(slotKey, label, p.pct);
  };

  Controller.prototype.showFormError = function (message) {
    var el = this.ui.formError;
    if (!el) {
      var first = Object.keys(this.slots)[0];
      if (first) this.showSlotError(first, message);
      return;
    }
    el.textContent = message || '';
    el.hidden = !message;
  };

  Controller.prototype.clearFormError = function () {
    this.showFormError('');
  };

  Controller.prototype.showSlotError = function (slotKey, message) {
    var ui = this.slotUi(slotKey);
    if (!ui || !ui.error) {
      this.showFormError(message);
      return;
    }
    ui.error.textContent = message || '';
    ui.error.hidden = !message;
    if (message) {
      ui.root.classList.add('fab-portrait--needs-photo');
      this.el.classList.add('fab-portrait--needs-photo');
    } else {
      ui.root.classList.remove('fab-portrait--needs-photo');
    }
  };

  Controller.prototype.clearError = function (slotKey) {
    if (slotKey) this.showSlotError(slotKey, '');
    this.clearFormError();
    this.el.classList.remove('fab-portrait--needs-photo');
  };

  Controller.prototype.setBusy = function (slotKey, busy) {
    this.busy = !!busy;
    var keys = slotKey ? [slotKey] : Object.keys(this.slots);
    var self = this;
    keys.forEach(function (key) {
      var ui = self.slotUi(key);
      if (!ui) return;
      if (ui.uploadBtn) ui.uploadBtn.disabled = self.busy;
      if (ui.changeBtn) ui.changeBtn.disabled = self.busy;
      if (ui.removeBtn) ui.removeBtn.disabled = self.busy;
    });
  };

  Controller.prototype.revokeLocalThumb = function (slotKey) {
    var url = this.localThumbs[slotKey];
    if (!url) return;
    try { URL.revokeObjectURL(url); } catch (_) { /* noop */ }
    this.localThumbs[slotKey] = '';
  };

  /* ---------------- inputs ---------------- */

  Controller.prototype.applyStateToInputs = function () {
    if (this.universal) {
      this.applyUniversalInputs();
      this.validateFields();
      return;
    }
    if (this.ui.name) this.ui.name.value = this.state.coverTitle || '';
    var choice = this.state.backgroundChoice || 'director';
    var matched = false;
    this.ui.bgRadios.forEach(function (r) {
      r.checked = r.value === choice;
      if (r.checked) matched = true;
    });
    if (!matched) {
      this.state.backgroundChoice = 'director';
      this.ui.bgRadios.forEach(function (r) { r.checked = r.value === 'director'; });
    }
    if (this.ui.other) this.ui.other.value = this.state.backgroundOther || '';
    this.toggleOther(false);
    this.validateText();
  };

  Controller.prototype.applyUniversalInputs = function () {
    var values = this.state.values || {};
    var self = this;
    $$(this.el, '[data-fab-portrait-field]').forEach(function (wrap) {
      var key = wrap.getAttribute('data-field-key');
      var type = (wrap.getAttribute('data-field-type') || 'text').toLowerCase();
      var stored = values[key] == null ? '' : String(values[key]);
      if (type === 'colour') {
        self.applyColourValue(wrap, stored);
        return;
      }
      var input = $(wrap, '[data-fab-portrait-value]');
      if (input) input.value = stored;
    });
  };

  Controller.prototype.applyColourValue = function (wrap, stored) {
    var def = wrap.getAttribute('data-field-default') || '';
    var radios = $$(wrap, '[data-fab-portrait-colour]');
    var other = $(wrap, '[data-fab-portrait-colour-other]');
    var otherWrap = $(wrap, '[data-fab-portrait-other-wrap]');
    var value = String(stored || '');
    var matched = false;
    radios.forEach(function (r) {
      if (value && r.value === value) {
        r.checked = true;
        matched = true;
      } else {
        r.checked = false;
      }
    });
    if (!matched) {
      if (!value || value === def) {
        radios.forEach(function (r) { r.checked = r.value === '__director__'; });
        if (other) other.value = '';
        if (otherWrap) otherWrap.hidden = true;
      } else {
        radios.forEach(function (r) { r.checked = r.value === '__other__'; });
        if (other) other.value = value;
        if (otherWrap) otherWrap.hidden = false;
      }
    } else if (otherWrap) {
      otherWrap.hidden = true;
    }
  };

  Controller.prototype.toggleOther = function (focus) {
    var isOther = this.state.backgroundChoice === 'other';
    if (this.ui.otherWrap) this.ui.otherWrap.hidden = !isOther;
    if (isOther && focus && this.ui.other) this.ui.other.focus();
  };

  Controller.prototype.toggleColourOther = function (wrap, focus) {
    var checked = $(wrap, '[data-fab-portrait-colour]:checked');
    var isOther = checked && checked.value === '__other__';
    var otherWrap = $(wrap, '[data-fab-portrait-other-wrap]');
    var other = $(wrap, '[data-fab-portrait-colour-other]');
    if (otherWrap) otherWrap.hidden = !isOther;
    if (isOther && focus && other) other.focus();
  };

  Controller.prototype.validateText = function () {
    var title = Core.normaliseCoverTitle(this.state.coverTitle);
    if (this.ui.name) this.ui.name.setAttribute('aria-invalid', title.valid ? 'false' : 'true');
    var bg = Core.resolveBackground(this.state.backgroundChoice, this.state.backgroundOther);
    if (this.ui.other) this.ui.other.setAttribute('aria-invalid', bg.valid ? 'false' : 'true');
    return title.valid && bg.valid;
  };

  Controller.prototype.collectUniversalValues = function () {
    var values = {};
    var valid = true;
    var firstInvalid = null;
    var self = this;
    $$(this.el, '[data-fab-portrait-field]').forEach(function (wrap) {
      var key = wrap.getAttribute('data-field-key');
      var type = (wrap.getAttribute('data-field-type') || 'text').toLowerCase();
      var required = wrap.getAttribute('data-field-required') === 'true';
      var field = self.valueFields.filter(function (f) { return f.key === key; })[0] || {
        key: key,
        type: type,
        required: required,
        default: wrap.getAttribute('data-field-default') || '',
        max_length: Number(wrap.getAttribute('data-max-length')) || 0,
      };
      var raw = '';
      if (type === 'colour') {
        var checked = $(wrap, '[data-fab-portrait-colour]:checked');
        var other = $(wrap, '[data-fab-portrait-colour-other]');
        if (required && !checked) {
          valid = false;
          if (!firstInvalid) firstInvalid = wrap;
          values[key] = '';
          return;
        }
        var resolved = Core.resolveColourValue(
          checked ? checked.value : '__director__',
          other ? other.value : '',
          field
        );
        raw = resolved.value;
        if (!resolved.valid || (required && resolved.isDefault && (!checked || checked.value === '__director__'))) {
          valid = false;
          if (other) other.setAttribute('aria-invalid', 'true');
          if (!firstInvalid) firstInvalid = other || wrap;
        } else if (other) {
          other.setAttribute('aria-invalid', 'false');
        }
      } else {
        var input = $(wrap, '[data-fab-portrait-value]');
        raw = input ? input.value : '';
        var check = Core.validateFieldValue(field, raw);
        raw = check.value;
        if (input) input.setAttribute('aria-invalid', check.valid ? 'false' : 'true');
        if (!check.valid) {
          valid = false;
          if (!firstInvalid) firstInvalid = input || wrap;
        }
      }
      values[key] = raw;
    });
    this.state.values = values;
    return { values: values, valid: valid, firstInvalid: firstInvalid };
  };

  Controller.prototype.validateFields = function () {
    if (this.universal) return this.collectUniversalValues().valid;
    return this.validateText();
  };

  Controller.prototype.onTextChanged = function (how) {
    this.validateFields();
    this.syncProps();
    this.persist();
    this.invalidatePreviewAttachment();
    if (how === 'commit') {
      if (this._previewArtworkTimer) {
        clearTimeout(this._previewArtworkTimer);
        this._previewArtworkTimer = 0;
      }
      this.syncPrintPreview({ artworkChanged: true });
    } else {
      this.schedulePreviewArtworkRefresh();
    }
  };

  /* ---------------- line-item properties ---------------- */

  Controller.prototype.frameCode = function () {
    var scope = this.root;
    var checked = scope.querySelector('.fab-frame-radio:checked');
    var raw = '';
    if (checked) {
      var host = checked.closest('[data-frame-key]');
      raw = (host && host.dataset.frameKey) || checked.value || checked.dataset.frameValue || '';
    }
    if (!raw) {
      var legacy = scope.querySelector('fieldset.product-form__input--swatch input[type="radio"]:checked, fieldset.product-form__input--frame input[type="radio"]:checked');
      raw = legacy ? legacy.value : '';
    }
    if (window.FabCpCartMockup && window.FabCpCartMockup.normalizeFrameCode) {
      return raw ? window.FabCpCartMockup.normalizeFrameCode(raw) : '';
    }
    return String(raw || '').toLowerCase().trim();
  };

  Controller.prototype.syncLegacyProps = function () {
    this.state = Core.ensureCustomisationId(this.state);
    var props = Core.buildLineItemProperties(this.state, {
      styleKey: this.styleKey,
      styleVersion: this.styleVersion,
      sourceProductId: this.productId,
      frameCode: this.frameCode(),
    });
    var purchasable = this.isPurchasable();
    this.ui.props.forEach(function (input) {
      var key = input.dataset.fabPortraitProp;
      var value = Object.prototype.hasOwnProperty.call(props, key) ? props[key] : '';
      input.value = value == null ? '' : String(value);
      input.disabled = !purchasable;
    });
  };

  Controller.prototype.stampPostedProperties = function (props) {
    var host = this.ui.propsHost;
    if (!host || !props || typeof props !== 'object') return;
    host.textContent = '';
    Object.keys(props).forEach(function (key) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.setAttribute('form', this.formId);
      input.name = 'properties[' + key + ']';
      var val = props[key];
      input.value = val == null ? '' : String(val);
      host.appendChild(input);
    }, this);
  };

  Controller.prototype.clearPostedProperties = function () {
    if (this.ui.propsHost) this.ui.propsHost.textContent = '';
  };

  Controller.prototype.syncProps = function () {
    if (this.universal) {
      this.collectUniversalValues();
      this.state = Core.ensureCustomisationId(this.state, { universal: true });
      return;
    }
    this.syncLegacyProps();
  };

  Controller.prototype.requiredImageKeys = function () {
    if (!this.universal) return ['photo'];
    return this.imageFields.filter(function (f) { return !!f.required; }).map(function (f) { return f.key; });
  };

  Controller.prototype.isPurchasable = function () {
    if (this.universal) {
      if (!this.configReady || !this.state.configHash) return false;
      if (!this.validateFields()) return false;
      var uploads = this.state.uploads || {};
      var required = this.requiredImageKeys();
      for (var i = 0; i < required.length; i++) {
        if (!Core.slotIsReady(uploads[required[i]])) return false;
      }
      var keys = Object.keys(this.slots);
      for (var s = 0; s < keys.length; s++) {
        var rec = uploads[keys[s]];
        if (rec && !rec.verified) return false;
      }
      return true;
    }
    var u = this.state.upload;
    return !!(u && u.verified && Core.slotIsReady(u) && this.validateText());
  };

  /* ---------------- sticky bar thumbnail ---------------- */

  Controller.prototype.firstThumbSrc = function () {
    var keys = this.universal
      ? this.imageFields.map(function (f) { return f.key; })
      : ['photo'];
    if (!keys.length) keys = Object.keys(this.slots);
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (this.localThumbs[key]) return { src: this.localThumbs[key], id: (this.state.uploads[key] && this.state.uploads[key].uploadId) || '' };
      var u = this.state.uploads[key] || (key === 'photo' ? this.state.upload : null);
      if (u && u.previewUrl) return { src: u.previewUrl, id: u.uploadId || '' };
    }
    return { src: '', id: '' };
  };

  Controller.prototype.syncStickyThumb = function () {
    var bar = this.root.querySelector('[data-product-sticky-cart-bar]');
    var mockup = bar && bar.querySelector('[data-fab-cp-sticky-mockup]');
    if (!mockup) return;
    var thumb = this.firstThumbSrc();
    mockup.dataset.mode = 'portrait';
    mockup.dataset.previewUrl = thumb.src;
    mockup.dataset.uploadId = thumb.src ? thumb.id : '';
    mockup.hidden = false;
    mockup.dataset.fabCpMockupReady = '';
    if (window.FabCpCartMockup) window.FabCpCartMockup.initAll(this.root);
  };

  /* ---------------- upload flow ---------------- */

  Controller.prototype.clearUpload = function (slotKey) {
    slotKey = slotKey || 'photo';
    this.slotTokens[slotKey] = (this.slotTokens[slotKey] || 0) + 1;
    delete this.state.uploads[slotKey];
    if (slotKey === 'photo') this.state.upload = null;
    this.revokeLocalThumb(slotKey);
    this.showUploadRow(slotKey);
    this.setStatus(slotKey, '', '');
    this.clearPostedProperties();
    this.syncStickyThumb();
    this.syncProps();
    this.persist();
    this.syncPrintPreview();
    var ui = this.slotUi(slotKey);
    if (ui && ui.fileInput) ui.fileInput.value = '';
  };

  Controller.prototype.clearAllUploads = function () {
    Object.keys(this.slots).forEach(function (key) {
      this.clearUpload(key);
    }, this);
  };

  Controller.prototype.afterApproved = function (slotKey) {
    var u = this.state.uploads[slotKey];
    var ui = this.slotUi(slotKey);
    if (u && u.previewUrl && !this.localThumbs[slotKey] && ui && ui.previewImg) ui.previewImg.src = u.previewUrl;
    this.hideProgress(slotKey);
    this.setStatus(slotKey, 'ok', this.t('photo_status_received'));
    this.clearError(slotKey);
    this.syncStickyThumb();
    this.syncProps();
    this.persist();
    this.syncPrintPreview();
  };

  Controller.prototype.handleFile = async function (file, slotKey) {
    slotKey = slotKey || 'photo';
    if (!file || this.busy) return;
    this.clearError(slotKey);

    var check = Core.validateFile(file);
    if (!check.ok) {
      this.showSlotError(slotKey, this.t('error_' + check.error));
      return;
    }
    if (!this.uploader) {
      this.showSlotError(slotKey, this.t('error_upload_failed'));
      return;
    }
    if (this.universal && !this.configReady) {
      var ok = await this.loadRemoteConfig();
      if (!ok) return;
    }

    var token = ++this.slotTokens[slotKey];
    this.progressSlot = slotKey;
    this.setBusy(slotKey, true);
    delete this.state.uploads[slotKey];
    if (slotKey === 'photo') this.state.upload = null;
    this.clearPostedProperties();
    this.syncProps();
    this._previewPending = null;
    this._previewPendingFrame = false;
    this.teardownPrintPreview();
    this.syncPrintPreview();

    var decoded = null;
    var preview = null;
    try {
      this.showProgress(slotKey, this.t('progress_preparing'), 2);
      decoded = await decodeFile(file, check.mime, this.heicDecoderUrl);
      preview = await buildPreview(decoded);
    } catch (err) {
      if (decoded && decoded.revoke) decoded.revoke();
      if (token !== this.slotTokens[slotKey]) return;
      this.hideProgress(slotKey);
      this.setBusy(slotKey, false);
      this.syncPrintPreview();
      this.showSlotError(slotKey, this.t('error_decode_failed'));
      return;
    }
    if (decoded && decoded.revoke) decoded.revoke();
    if (token !== this.slotTokens[slotKey]) return;

    this.revokeLocalThumb(slotKey);
    this.localThumbs[slotKey] = URL.createObjectURL(preview.blob);
    this.showPreview(slotKey, this.localThumbs[slotKey], 'checking', this.t('photo_status_checking'));
    this.syncStickyThumb();
    this.syncPrintPreview();

    var body;
    try {
      body = await this.uploader.uploadPortrait({
        originalFile: file,
        previewBlob: preview.blob,
        previewWidth: preview.width,
        previewHeight: preview.height,
        imageWidth: decoded.width,
        imageHeight: decoded.height,
        mode: this.universal ? 'personalised' : 'portrait',
        orientation: this.outputOrientation || 'portrait',
        productId: this.universal ? this.productId : '',
        uploadSlot: this.universal ? slotKey : '',
        configHash: this.universal ? this.state.configHash : '',
      });
    } catch (err) {
      if (token !== this.slotTokens[slotKey]) return;
      console.warn('[fab-portrait] upload failed', err && err.message);
      this.setBusy(slotKey, false);
      var uploadErr = String((err && err.message) || '');
      if (/configuration_changed|upload_configuration_mismatch/.test(uploadErr)) {
        await this.onConfigurationInvalid(uploadErr);
      return;
    }
      this.failUpload(slotKey, this.t('error_upload_failed'));
      return;
    }
    if (token !== this.slotTokens[slotKey]) return;

    var upload = Core.sanitiseUploadRecord(Core.mergeUploadResponse({ fileName: file.name || '' }, body));
    var decision = window.FabCpUploader.decideStatus({ ok: true, httpStatus: 200, body: body });

    if (decision === 'pending') {
      var self = this;
      var polled = await this.uploader.pollPortrait(upload.uploadId, {
        isCancelled: function () { return token !== self.slotTokens[slotKey]; },
      });
      if (token !== this.slotTokens[slotKey]) return;
      decision = polled.decision;
      if (polled.result && polled.result.body) {
        upload = Core.sanitiseUploadRecord(Core.mergeUploadResponse(upload, polled.result.body));
      }
    }

    this.setBusy(slotKey, false);

    if (decision === 'approved') {
      upload.verified = !!(Core.isUuid(upload.uploadId) && upload.receipt);
      if (!upload.verified) {
        this.failUpload(slotKey, this.t('error_verify_failed'));
        return;
      }
      this.state.uploads[slotKey] = upload;
      if (slotKey === 'photo') this.state.upload = upload;
      this.cacheCartPreview(upload.uploadId, preview.blob);
      this.afterApproved(slotKey);
      return;
    }
    if (decision === 'rejected') { this.failUpload(slotKey, this.t('error_rejected')); return; }
    if (decision === 'timeout') { this.failUpload(slotKey, this.t('error_timeout')); return; }
    if (decision === 'expired' || decision === 'missing') { this.failUpload(slotKey, this.t('error_expired')); return; }
    this.failUpload(slotKey, this.t('error_verify_failed'));
  };

  Controller.prototype.failUpload = function (slotKey, message) {
    delete this.state.uploads[slotKey];
    if (slotKey === 'photo') this.state.upload = null;
    this.revokeLocalThumb(slotKey);
    this.hideProgress(slotKey);
    this.showUploadRow(slotKey);
    this.syncStickyThumb();
    this.syncProps();
    this.persist();
    this.syncPrintPreview();
    var ui = this.slotUi(slotKey);
    if (ui && ui.fileInput) ui.fileInput.value = '';
    this.showSlotError(slotKey, message);
  };

  Controller.prototype.cacheCartPreview = function (uploadId, blob) {
    if (!window.FabCpLibrary || !uploadId || !blob) return;
    try {
      var lib = new window.FabCpLibrary();
      if (lib.saveCartPreview) lib.saveCartPreview(uploadId, blob).catch(function () {});
    } catch (_) { /* optional */ }
  };

  Controller.prototype.onConfigurationInvalid = async function (kind) {
    this.clearAllUploads();
    await this.loadRemoteConfig();
    var key = /upload_configuration_mismatch/.test(String(kind || ''))
      ? 'error_upload_mismatch'
      : 'error_configuration_changed';
    this.showFormError(this.t(key));
    this.scrollToConfigurator();
  };

  /* ---------------- add-to-cart gate ---------------- */

  Controller.prototype.gateSubmit = function (evt) {
    var form = evt.target;
    // A variant input named "id" shadows HTMLFormElement.id. Compare nodes.
    if (!form || form !== this.formEl()) return;
    if (this.passSubmit) {
      this.passSubmit = false;
      return;
    }

    if (this.universal) {
      this.block(evt);
      this.prepareThenSubmit(form);
      return;
    }

    if (!this.validateText()) {
      this.block(evt);
      var bad = this.ui.name && this.ui.name.getAttribute('aria-invalid') === 'true' ? this.ui.name : this.ui.other;
      if (bad) bad.focus();
      return;
    }

    var u = this.state.upload;
    if (!u) {
      this.block(evt);
      this.el.classList.add('fab-portrait--needs-photo');
      this.showSlotError('photo', this.t('error_photo_required'));
      this.scrollToConfigurator();
      return;
    }
    if (this.busy || !u.verified) {
      this.block(evt);
      this.showSlotError('photo', this.t('error_pending'));
      this.scrollToConfigurator();
      if (!this.busy && !u.verified) this.verifyRestoredUpload('photo');
      return;
    }
    if (!Core.slotIsReady(u) && !Core.uploadHasRequiredValues(u)) {
      this.block(evt);
      this.clearUpload('photo');
      this.showSlotError('photo', this.t('error_expired'));
      this.scrollToConfigurator();
      return;
    }
    this.syncProps();
  };

  Controller.prototype.prepareThenSubmit = async function (form) {
    if (this.preparing) return;
    var collected = this.collectUniversalValues();
    if (!collected.valid) {
      this.showFormError(this.t('error_required_field'));
      if (collected.firstInvalid && collected.firstInvalid.focus) collected.firstInvalid.focus();
      this.scrollToConfigurator();
      return;
    }

    if (!this.configReady) {
      var loaded = await this.loadRemoteConfig();
      if (!loaded) return;
    }

    var required = this.requiredImageKeys();
    for (var i = 0; i < required.length; i++) {
      var rec = this.state.uploads[required[i]];
      if (!rec) {
        this.el.classList.add('fab-portrait--needs-photo');
        this.showSlotError(required[i], this.t('error_photo_required'));
        this.scrollToConfigurator();
        return;
      }
      if (this.busy || !rec.verified) {
        this.showSlotError(required[i], this.t('error_pending'));
        this.scrollToConfigurator();
        if (!this.busy && !rec.verified) this.verifyRestoredUpload(required[i]);
        return;
      }
      if (!Core.slotIsReady(rec)) {
        this.clearUpload(required[i]);
        this.showSlotError(required[i], this.t('error_expired'));
        this.scrollToConfigurator();
        return;
      }
    }

    var variantId = this.selectedVariantId();
    if (!variantId) {
      this.showFormError(this.t('error_prepare_failed'));
      return;
    }

    this.state = Core.ensureCustomisationId(this.state, { universal: true });
    var endpoint = (window.fab && window.fab.prepareEndpoint) || '';
    if (!endpoint) {
      this.showFormError(this.t('error_config_unavailable'));
      return;
    }

    this.preparing = true;
    this.setBusy('', true);
    try {
      this._previewCartFrozen = true;
      this.syncPrintPreview();
      var payload = {
        product_id: String(this.productId),
        variant_id: String(variantId),
        customisation_id: this.state.customisationId,
        config_hash: this.state.configHash,
        values: collected.values,
        uploads: Core.uploadsPayload(this.state.uploads),
      };
      var prepared = this._previewFrozenPayload;
      if (!prepared && this.printPreview && this.printPreview.state && this.printPreview.state.selected) {
        try {
          prepared = this.printPreview.preparePayload(this.previewInput());
        } catch (_) { prepared = null; }
      }
      if (prepared && prepared.preview_id) {
        payload.preview_id = prepared.preview_id;
        if (prepared.customisation_id) payload.customisation_id = prepared.customisation_id;
        this._previewFrozenPayload = {
          preview_id: prepared.preview_id,
          customisation_id: prepared.customisation_id,
        };
      }
      var res = await fetch(endpoint, {
        method: 'POST',
        headers: Object.assign(this.authHeaders(), { 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
      });
      var body = null;
      try { body = await res.json(); } catch (_) { body = null; }
      if (!res.ok) {
        this._previewCartFrozen = false;
        var kind = Core.parsePrepareError(res.status, body);
        if (kind === 'configuration_changed' || kind === 'upload_configuration_mismatch') {
          await this.onConfigurationInvalid(kind);
          return;
        }
        this.showFormError(this.t('error_prepare_failed'));
        this.scrollToConfigurator();
        return;
      }
      var props = body && body.properties;
      if (!props || typeof props !== 'object') {
        this._previewCartFrozen = false;
        this.showFormError(this.t('error_prepare_failed'));
        return;
      }
      this.stampPostedProperties(props);
      this.clearFormError();
      this.passSubmit = true;
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    } catch (_) {
      this._previewCartFrozen = false;
      this.showFormError(this.t('error_prepare_failed'));
      this.scrollToConfigurator();
    } finally {
      this.preparing = false;
      this.setBusy('', false);
      this._previewCartFrozen = false;
    }
  };

  Controller.prototype.block = function (evt) {
    evt.preventDefault();
    evt.stopImmediatePropagation();
  };

  Controller.prototype.scrollToConfigurator = function () {
    try {
      var rect = this.el.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        this.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } catch (_) { /* noop */ }
  };

  /* ---------------- events ---------------- */

  Controller.prototype.bindSlot = function (slotKey) {
    var self = this;
    var ui = this.slotUi(slotKey);
    if (!ui) return;

    if (ui.uploadBtn && ui.fileInput) {
      ui.uploadBtn.addEventListener('click', function () {
        ui.fileInput.value = '';
        ui.fileInput.click();
      });
    }
    if (ui.uploadRow) {
      ['dragenter', 'dragover'].forEach(function (ev) {
        ui.uploadRow.addEventListener(ev, function (e) {
          e.preventDefault();
          e.stopPropagation();
          ui.uploadRow.classList.add('is-dragover');
        });
      });
      ['dragleave', 'drop'].forEach(function (ev) {
        ui.uploadRow.addEventListener(ev, function (e) {
          e.preventDefault();
          e.stopPropagation();
          ui.uploadRow.classList.remove('is-dragover');
        });
      });
      ui.uploadRow.addEventListener('drop', function (e) {
        var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (f) self.handleFile(f, slotKey);
      });
    }
    if (ui.changeBtn && ui.fileInput) {
      ui.changeBtn.addEventListener('click', function () {
        ui.fileInput.value = '';
        ui.fileInput.click();
      });
    }
    if (ui.removeBtn) {
      ui.removeBtn.addEventListener('click', function () {
        self.clearError(slotKey);
        self.clearUpload(slotKey);
        if (ui.uploadBtn) ui.uploadBtn.focus();
      });
    }
    if (ui.fileInput) {
      ui.fileInput.addEventListener('change', function () {
        var f = ui.fileInput.files && ui.fileInput.files[0];
        if (f) self.handleFile(f, slotKey);
      });
    }
  };

  Controller.prototype.bind = function () {
    var self = this;
    Object.keys(this.slots).forEach(function (key) { self.bindSlot(key); });

    if (this.universal) {
      $$(this.el, '[data-fab-portrait-value]').forEach(function (input) {
        input.addEventListener('input', function () { self.onTextChanged('input'); });
        input.addEventListener('change', function () { self.onTextChanged('commit'); });
      });
      $$(this.el, '[data-fab-portrait-field][data-field-type="colour"]').forEach(function (wrap) {
        $$(wrap, '[data-fab-portrait-colour]').forEach(function (r) {
          r.addEventListener('change', function () {
            if (!r.checked) return;
            self.toggleColourOther(wrap, true);
            self.onTextChanged('commit');
          });
        });
        var other = $(wrap, '[data-fab-portrait-colour-other]');
        if (other) other.addEventListener('input', function () { self.onTextChanged('input'); });
      });
    } else {
      if (this.ui.name) {
        this.ui.name.addEventListener('input', function () {
          self.state.coverTitle = self.ui.name.value;
          self.onTextChanged('input');
        });
      }
      this.ui.bgRadios.forEach(function (r) {
      r.addEventListener('change', function () {
        if (!r.checked) return;
        self.state.backgroundChoice = r.value;
        self.toggleOther(true);
          self.onTextChanged('commit');
      });
    });
      if (this.ui.other) {
        this.ui.other.addEventListener('input', function () {
          self.state.backgroundOther = self.ui.other.value;
          self.onTextChanged('input');
        });
      }
    }

    this.root.addEventListener('submit', function (evt) { self.gateSubmit(evt); }, true);

    this.root.addEventListener('click', function (e) {
      var skip = e.target && e.target.closest && e.target.closest('[data-fab-print-preview-skip]');
      if (skip) {
        e.preventDefault();
        self.skipPrintPreview();
        return;
      }
      var retry = e.target && e.target.closest && e.target.closest('[data-fab-print-preview-retry-close], [data-fab-print-preview-retry-room]');
      if (retry) {
        e.preventDefault();
        if (retry.hasAttribute('data-fab-print-preview-retry-room')) self.retryPreviewRoom();
        else self.retryPreviewClose();
      }
    });

    this.root.addEventListener('variant:change', function () {
      self.syncProps();
      self.syncStickyThumb();
      self.syncPrintPreview({ variantChanged: true });
    });
    this.root.addEventListener('change', function (e) {
      if (e.target && e.target.matches && e.target.matches('.fab-frame-radio, fieldset.product-form__input input[type="radio"]')) {
        self.syncProps();
        self.syncPrintPreview({ variantChanged: true });
      }
    });

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) self.fetchPreviewQuota();
    });
    window.addEventListener('focus', function () { self.fetchPreviewQuota(); });

    window.addEventListener('pagehide', function () {
      Object.keys(self.localThumbs).forEach(function (key) { self.revokeLocalThumb(key); });
      self.teardownPrintPreview();
    });
  };

  /* ---------------- print preview ---------------- */

  Controller.prototype.previewBaseUrl = function () {
    var fab = window.fab || {};
    if (fab.previewBaseUrl) return String(fab.previewBaseUrl).replace(/\/$/, '');
    var prepare = String(fab.prepareEndpoint || '');
    var idx = prepare.indexOf('/functions/v1');
    if (idx > 0) return prepare.slice(0, idx);
    return 'https://ylccvrpczrbnjzkoacej.supabase.co';
  };

  Controller.prototype.previewSessionToken = function () {
    return (this.uploader && this.uploader.sessionToken)
      || (window.FabCpUploader && window.FabCpUploader.getOrCreateSessionToken && window.FabCpUploader.getOrCreateSessionToken())
      || '';
  };

  Controller.prototype.previewStorageKey = function (token) {
    return 'fab-print-preview:' + String(this.productId || 'p') + ':' + String(token || 'anon');
  };

  Controller.prototype.previewCopy = function () {
    return readJsonScript(this.el, 'script[data-fab-print-preview-copy]');
  };

  Controller.prototype.previewGetHeaders = function () {
    return this.authHeaders();
  };

  Controller.prototype.previewInput = function () {
    var values = this.universal
      ? (this.collectUniversalValues().values || {})
      : this.state.values || {};
    return {
      product_id: String(this.productId),
      variant_id: String(this.selectedVariantId() || ''),
      config_hash: this.state.configHash || '',
      values: values,
      uploads: this.previewUploadsPayload(),
    };
  };

  Controller.prototype.previewImageKeys = function () {
    if (this.universal) return this.imageFields.map(function (f) { return f.key; });
    return ['photo'];
  };

  Controller.prototype.previewSlotRecord = function (slotKey) {
    return (this.state.uploads && this.state.uploads[slotKey])
      || (slotKey === 'photo' ? this.state.upload : null)
      || null;
  };

  Controller.prototype.previewSlotIsClean = function (slotKey) {
    var fn = Core.slotIsScreenedClean;
    if (typeof fn === 'function') return fn(this.previewSlotRecord(slotKey));
    var rec = this.previewSlotRecord(slotKey);
    if (!rec || !Core.slotIsReady(rec)) return false;
    if (rec.requiresManualReview) return false;
    return rec.status === 'screened_clean';
  };

  Controller.prototype.previewUploadsPayload = function () {
    var all = Core.uploadsPayload(this.state.uploads);
    var out = {};
    var keys = Object.keys(all);
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      if (this.previewSlotIsClean(k)) out[k] = all[k];
    }
    return out;
  };

  Controller.prototype.previewArtworkKey = function (input) {
    var Api = window.FabPrintPreview;
    var slots = this.previewImageKeys();
    if (Api && typeof Api.previewArtworkKey === 'function') return Api.previewArtworkKey(input, slots);
    var uploads = {};
    var src = (input && input.uploads) || {};
    (slots.length ? slots : Object.keys(src)).forEach(function (k) {
      uploads[k] = (src[k] && src[k].upload_id) || '';
    });
    Object.keys(src).forEach(function (k) {
      if (!(k in uploads)) uploads[k] = (src[k] && src[k].upload_id) || '';
    });
    return JSON.stringify({
      product_id: input.product_id,
      config_hash: input.config_hash,
      values: input.values,
      uploads: uploads,
    });
  };

  Controller.prototype.previewRequiredImagesClean = function () {
    var required = this.requiredImageKeys();
    if (!required.length) {
      var keys = this.previewImageKeys();
      for (var i = 0; i < keys.length; i++) {
        if (this.previewSlotIsClean(keys[i])) return true;
      }
      return false;
    }
    for (var r = 0; r < required.length; r++) {
      if (!this.previewSlotIsClean(required[r])) return false;
    }
    return true;
  };

  Controller.prototype.previewHasFlaggedUpload = function () {
    var uploads = this.state.uploads || {};
    var keys = Object.keys(uploads);
    for (var i = 0; i < keys.length; i++) {
      var rec = uploads[keys[i]];
      if (!rec || !rec.uploadId) continue;
      if (!this.previewSlotIsClean(keys[i])) return true;
    }
    return false;
  };

  Controller.prototype.previewCanOffer = function () {
    if (!this.previewEnabled || !this.universal || !this.configReady) return false;
    if (!this.state.configHash || !this.selectedVariantId()) return false;
    if (!this.previewImageKeys().length) return false;
    if (!this.validateFields()) return false;
    if (!this.previewRequiredImagesClean()) return false;
    if (this.previewHasFlaggedUpload()) return false;
    return true;
  };

  Controller.prototype.previewIsChecking = function () {
    return !!(this.previewEnabled && this.busy && !this.previewRequiredImagesClean());
  };

  Controller.prototype.previewGallery = function () {
    return this.root && this.root.querySelector('fab-gallery');
  };

  Controller.prototype.previewPhotoUrls = function () {
    var keys = this.previewImageKeys();
    if (!keys.length) keys = ['photo'];
    var urls = [];
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      var rec = this.previewSlotRecord(key) || {};
      var url = this.localThumbs[key] || rec.previewUrl || '';
      if (url) urls.push(url);
    }
    return urls;
  };

  Controller.prototype.previewPhotoUrl = function () {
    var urls = this.previewPhotoUrls();
    return urls[0] || '';
  };

  Controller.prototype.previewHasField = function (key) {
    return this.valueFields.some(function (f) { return f && f.key === key; });
  };

  Controller.prototype.previewPalette = function (values) {
    var bg = '';
    if (this.previewHasField('background')) bg = String((values && values.background) || '');
    var HEX = /^#([0-9A-Fa-f]{6})$/;
    var known = {
      Peach: { colour: '#F4C7A8', accent: '#F2E7D8' },
      'Pale pink': { colour: '#F3D2D8', accent: '#F2E7D8' },
      'Sky blue': { colour: '#BFD9EE', accent: '#F2E7D8' },
      Sage: { colour: '#C3CEB9', accent: '#F2E7D8' },
    };
    var pair = known[bg] || { colour: '#DCC9BF', accent: '#F2E7D8' };
    if (!HEX.test(pair.colour)) pair.colour = '#DCC9BF';
    if (!HEX.test(pair.accent)) pair.accent = '#F2E7D8';
    return pair;
  };

  Controller.prototype.previewWash = function (values) {
    return this.previewPalette(values).colour;
  };

  Controller.prototype.previewName = function () {
    if (!this.previewHasField('name')) return '';
    var values = this.previewInput().values || {};
    return String(values.name || '').trim();
  };

  Controller.prototype.previewGeneratingCopy = function () {
    var copy = this.previewCopy();
    var name = this.previewName();
    if (!name) return copy.generating || 'Creating your preview…';
    var tpl = copy.generating_named || "Creating {{ name }}’s preview…";
    var chars = Array.from(name);
    var safe = chars.length > 24 ? chars.slice(0, 23).join('') + '…' : name;
    return this.interpolatePreview(tpl, { name: safe });
  };

  Controller.prototype.interpolatePreview = function (tpl, vars) {
    var Api = window.FabPrintPreview;
    if (Api && typeof Api.interpolateCopy === 'function') return Api.interpolateCopy(tpl, vars);
    return String(tpl || '').replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, key) {
      return vars && vars[key] != null ? String(vars[key]) : '';
    });
  };

  Controller.prototype.previewPlainText = function (value) {
    var Api = window.FabPrintPreview;
    if (Api && typeof Api.plainText === 'function') return Api.plainText(value);
    return String(value == null ? '' : value).replace(/<[^>]*>/g, '');
  };

  Controller.prototype.applyPreviewQuota = function (quota, sentAt) {
    if (!quota || typeof quota !== 'object') return;
    var Api = window.FabPrintPreview;
    var newer = true;
    if (Api && typeof Api.shouldApplyQuota === 'function') {
      newer = Api.shouldApplyQuota(this._quotaAt, sentAt);
    } else if (this._quotaAt && sentAt != null && sentAt < this._quotaAt) {
      newer = false;
    }
    if (!newer) return;
    this._quotaAt = sentAt != null ? sentAt : Date.now();
    this.previewQuota = quota;
    this.scheduleQuotaReset();
    this.renderPreviewOffer();
  };

  Controller.prototype.scheduleQuotaReset = function () {
    var self = this;
    if (this._quotaTimer) {
      clearTimeout(this._quotaTimer);
      this._quotaTimer = 0;
    }
    var resets = this.previewQuota && this.previewQuota.resets_at;
    if (!resets) return;
    var when = Date.parse(resets);
    if (!when || when <= Date.now()) return;
    this._quotaTimer = setTimeout(function () {
      self._quotaTimer = 0;
      self.fetchPreviewQuota();
    }, Math.min(when - Date.now() + 250, 86400000));
  };

  Controller.prototype.fetchPreviewQuota = function () {
    var self = this;
    if (!this.previewEnabled) return Promise.resolve(null);
    var Api = window.FabPrintPreview;
    var run = function (preview) {
      if (!preview || typeof preview.fetchQuota !== 'function') return Promise.resolve(null);
      return preview.fetchQuota().then(function (data) {
        self.applyPreviewQuota(data && data.preview_quota, data && data._sentAt);
        return data;
      }).catch(function (err) {
        if (err && err.previewQuota) self.applyPreviewQuota(err.previewQuota, err.sentAt);
        return null;
      });
    };
    if (this.printPreview && typeof this.printPreview.fetchQuota === 'function') return run(this.printPreview);
    if (Api && typeof Api.FabPrintPreview === 'function') {
      try {
        var tmp = new Api.FabPrintPreview({
          baseUrl: this.previewBaseUrl(),
          getHeaders: function () { return self.previewGetHeaders(); },
          storageKey: this.previewStorageKey(this.previewSessionToken()),
        });
        return run(tmp);
      } catch (_) { /* headers missing */ }
    }
    return Promise.resolve(null);
  };

  Controller.prototype.previewQuotaRemaining = function () {
    var q = this.previewQuota;
    if (!q) return null;
    var n = Number(q.remaining);
    return isFinite(n) ? n : null;
  };

  Controller.prototype.previewPoolAvailable = function () {
    var q = this.previewQuota;
    if (!q) return true;
    return q.pool_available !== false;
  };

  Controller.prototype.previewHasMatchingJob = function () {
    if (!this.printPreview || !this.printPreview.state || !this.printPreview.state.preview_id) return false;
    var now = this.previewArtworkKey(this.previewInput());
    return !!(this.printPreview.key && this.printPreview.key === now);
  };

  Controller.prototype.previewAllowsNewArtwork = function (opts) {
    opts = opts || {};
    if (opts.frameOnly) return true;
    if (this.previewHasMatchingJob()) return true;
    if (!this.previewPoolAvailable()) return false;
    var remaining = this.previewQuotaRemaining();
    if (remaining != null && remaining <= 0) return false;
    return true;
  };

  Controller.prototype.previewSubjectPlural = function () {
    return this.previewImageKeys().length >= 2;
  };

  Controller.prototype.previewErrorCopy = function (s) {
    s = s || (this.printPreview && this.printPreview.state) || {};
    var copy = this.previewCopy();
    var code = String(s.error || '');
    var quota = s.preview_quota || this.previewQuota || {};
    var vars = {
      limit: quota.limit != null ? quota.limit : 3,
      remaining: quota.remaining != null ? quota.remaining : '',
      count: quota.remaining != null ? quota.remaining : '',
    };
    var tpl = '';
    if (code === 'preview_owner_limit') tpl = copy.limit || '';
    else if (code === 'preview_daily_limit') tpl = copy.error_daily_limit || '';
    else if (code === 'photo_needs_artist_review') tpl = copy.error_photo_review || '';
    else tpl = copy.error || '';
    var text = this.interpolatePreview(tpl, vars);
    if (!text) text = s.errorMessage || s.error || copy.error || '';
    return this.previewPlainText(text);
  };

  Controller.prototype.renderPreviewOffer = function () {
    var offers = this.el ? this.el.querySelectorAll('[data-fab-preview-offer]') : [];
    var enabled = !!this.previewEnabled;
    Array.prototype.forEach.call(offers, function (offer) {
      offer.hidden = !enabled;
    });
  };

  Controller.prototype.previewIsBusy = function () {
    var s = this.printPreview && this.printPreview.state;
    var Api = window.FabPrintPreview;
    if (Api && typeof Api.previewIsBusy === 'function') return Api.previewIsBusy(s && s.status);
    return !!(s && (s.status === 'queued' || s.status === 'generating' || s.status === 'rendering'));
  };

  Controller.prototype.invalidatePreviewAttachment = function () {
    this._previewFrozenPayload = null;
    if (this.printPreview && typeof this.printPreview.deselect === 'function') {
      try { this.printPreview.deselect(); } catch (_) { /* not ready */ }
    }
  };

  Controller.prototype.schedulePreviewArtworkRefresh = function () {
    var self = this;
    if (this._previewArtworkTimer) clearTimeout(this._previewArtworkTimer);
    this._previewArtworkTimer = setTimeout(function () {
      self._previewArtworkTimer = 0;
      self.syncPrintPreview({ artworkChanged: true });
    }, 1500);
  };

  Controller.prototype.skipPrintPreview = function () {
    if (this._previewArtworkTimer) {
      clearTimeout(this._previewArtworkTimer);
      this._previewArtworkTimer = 0;
    }
    this._previewPending = null;
    this._previewPendingFrame = false;
    this.invalidatePreviewAttachment();
    if (this.printPreview) this.printPreview.continueWithArtist();
    this.renderPrintPreviewChrome();
  };

  Controller.prototype.teardownPrintPreview = function () {
    if (this._previewArtworkTimer) {
      clearTimeout(this._previewArtworkTimer);
      this._previewArtworkTimer = 0;
    }
    this._previewPending = null;
    this._previewPendingFrame = false;
    this._previewStarting = false;
    this._previewFrameOnly = false;
    this._previewActivated = false;
    this._previewLastKey = '';
    this._previewRoomReady = false;
    this._previewCloseReady = false;
    this._previewDecoding = false;
    this._previewMotionStarted = false;
    this._previewResume = false;
    this._previewRevealEpoch += 1;
    this._previewRevealedId = '';
    this._previewCloseFailed = false;
    this.invalidatePreviewAttachment();
    if (this.printPreviewUnmount) {
      try { this.printPreviewUnmount(); } catch (_) { /* already gone */ }
    } else if (this.printPreview) {
      try { this.printPreview.destroy(); } catch (_) { /* already gone */ }
    }
    this.printPreviewUnmount = null;
    this.printPreview = null;
    this._previewMountedSession = '';
    this._previewVariantId = '';
    var gallery = this.previewGallery();
    if (gallery && typeof gallery.upsertGeneratedSlides === 'function') {
      gallery.upsertGeneratedSlides({ mode: 'none' });
    }
    this.renderPrintPreviewChrome();
  };

  Controller.prototype.ensurePrintPreview = function () {
    var self = this;
    var Api = window.FabPrintPreview;
    var host = this.el && this.el.querySelector('[data-fab-print-preview]');
    if (!host) return false;
    if (!Api || typeof Api.FabPrintPreview !== 'function') {
      if (!this._previewApiWaiting) {
        this._previewApiWaiting = true;
        var ticks = 0;
        var timer = setInterval(function () {
          ticks += 1;
          if (window.FabPrintPreview || ticks > 40) {
            clearInterval(timer);
            self._previewApiWaiting = false;
            if (window.FabPrintPreview) self.syncPrintPreview();
          }
        }, 50);
      }
      return false;
    }
    var token = this.previewSessionToken();
    if (this.printPreview && this.printPreviewUnmount && this._previewMountedSession !== token) {
      this.teardownPrintPreview();
    }
    if (this.printPreview) return true;
    this.printPreview = new Api.FabPrintPreview({
      baseUrl: this.previewBaseUrl(),
      getHeaders: function () { return self.previewGetHeaders(); },
      storageKey: this.previewStorageKey(token),
    });
    this.printPreviewUnmount = null;
    this._previewMountedSession = token;
    this.printPreview.addEventListener('change', function () { self.onPrintPreviewChange(); });
    return true;
  };

  Controller.prototype.previewStatusCopy = function (status, slow, frameOnly) {
    var copy = this.previewCopy();
    if (frameOnly && (status === 'rendering' || status === 'queued' || status === 'generating' || status === 'decoding')) {
      return copy.updating_frame || 'Updating frame…';
    }
    if (status === 'screening') return copy.screening || this.t('progress_checking');
    if (status === 'decoding') return copy.decoding || 'Getting your preview ready…';
    if (status === 'ready') return copy.ready || copy.slide_waiting || 'Your preview is ready';
    if (status === 'generating') return this.previewGeneratingCopy();
    if (status === 'queued' || status === 'rendering') return copy.rendering || copy.queued || 'Preparing your preview…';
    return copy[status] || copy.generating || 'Creating your preview…';
  };

  Controller.prototype.previewMediaBox = function (kind, s) {
    var Api = window.FabPrintPreview;
    if (Api && typeof Api.previewMediaSize === 'function') {
      return Api.previewMediaSize(kind, s, this.outputOrientation);
    }
    var width = Number(kind === 'room' ? s && s.room_width : s && s.close_width);
    var height = Number(kind === 'room' ? s && s.room_height : s && s.close_height);
    if (width > 0 && height > 0) return { width: width, height: height };
    var orient = String((s && s.output_orientation) || this.outputOrientation || 'portrait').toLowerCase();
    if (kind === 'room') return { width: 768, height: 960 };
    if (orient === 'landscape') return { width: 960, height: 720 };
    if (orient === 'square') return { width: 900, height: 900 };
    return { width: 720, height: 960 };
  };

  Controller.prototype.renderPrintPreviewChrome = function () {
    var host = this.el && this.el.querySelector('[data-fab-print-preview]');
    var live = host && host.querySelector('[data-fab-print-preview-live]');
    var skip = host && host.querySelector('[data-fab-print-preview-skip]');
    var copy = this.previewCopy();
    var s = this.printPreview && this.printPreview.state;
    var checking = this.previewIsChecking();
    var gallery = this.previewGallery();
    var inGallery = !!(gallery && typeof gallery.hasGeneratedPreview === 'function' && gallery.hasGeneratedPreview());
    var status = checking ? 'screening' : (this._previewDecoding ? 'decoding' : ((s && s.status) || ''));
    var message = this.previewStatusCopy(status, !!(s && s.slow), !!this._previewFrameOnly);
    if (s && s.slow && copy.slow) message = copy.slow;
    if (s && s.status === 'error') message = this.previewErrorCopy(s);
    if (!s && this.previewCanOffer() && !this.previewAllowsNewArtwork()) {
      message = this.previewErrorCopy({ error: this.previewPoolAvailable() ? 'preview_owner_limit' : 'preview_daily_limit', preview_quota: this.previewQuota, errorMessage: this.previewQuota && this.previewQuota.message });
    }
    if (live && live.textContent !== message) live.textContent = message || '';
    if (skip) {
      skip.textContent = copy.skip || '';
      var showSkip = !!(s && (s.slow || s.status === 'error'))
        || (this.previewCanOffer() && !this.previewAllowsNewArtwork());
      skip.hidden = !showSkip;
    }
  };

  Controller.prototype.renderPrintPreviewGallery = function (opts) {
    opts = opts || {};
    var gallery = this.previewGallery();
    if (!gallery || typeof gallery.upsertGeneratedSlides !== 'function') {
      this.renderPrintPreviewChrome();
      return;
    }
    var copy = this.previewCopy();
    var s = this.printPreview && this.printPreview.state;
    var checking = this.previewIsChecking();
    var photoUrls = this.previewPhotoUrls();
    var photoUrl = photoUrls[0] || '';
    var palette = this.previewPalette(this.previewInput().values);
    var support = this.previewSubjectPlural()
      ? (copy.supporting_pets || copy.supporting || '')
      : (copy.supporting || '');
    if (s && s.slow) support = copy.slow || support;
    var frameOnly = !!(opts.frameOnly || this._previewFrameOnly);
    var roomBox = this.previewMediaBox('room', s);
    var closeBox = this.previewMediaBox('close', s);
    var orientation = (s && s.output_orientation) || this.outputOrientation || 'portrait';

    if (s && s.status === 'skipped') {
      gallery.upsertGeneratedSlides({ mode: 'none' });
      this.renderPrintPreviewChrome();
      return;
    }

    var keepReady = !!(this._previewRoomReady && s && s.room_url && (
      s.selected || s.status === 'ready' || frameOnly
      || (s.status === 'error' && (s.error === 'preview_owner_limit' || s.error === 'preview_daily_limit') && s.room_url)
    ));
    if (keepReady) {
      gallery.upsertGeneratedSlides({
        mode: 'ready',
        activate: opts.activate,
        reveal: !!opts.reveal,
        immediate: !!opts.immediate || !!frameOnly,
        room: {
          src: s.room_url,
          alt: copy.roomAlt || '',
          label: copy.ready || copy.slide_room || copy.slide_waiting,
          caption: frameOnly
            ? (copy.updating_frame || copy.caption || copy.roomCaption || '')
            : ([copy.caption, copy.roomCaption].filter(Boolean).join(' ')),
          width: roomBox.width,
          height: roomBox.height,
          orientation: orientation,
        },
        close: {
          src: this._previewCloseReady ? s.close_url : '',
          alt: copy.closeAlt || '',
          label: copy.slide_close || copy.slide_waiting,
          status: this._previewCloseFailed
            ? (copy.close_error || copy.decoding || '')
            : (copy.decoding || ''),
          retryLabel: copy.close_retry || copy.skip || '',
          showRetry: !!this._previewCloseFailed,
          width: closeBox.width,
          height: closeBox.height,
          orientation: orientation,
        },
      });
      this.renderPrintPreviewChrome();
      return;
    }

    if (s && s.status === 'error') {
      gallery.upsertGeneratedSlides({
        mode: 'error',
        activate: false,
        motion: 'off',
        photoUrl: photoUrl,
        photoUrls: photoUrls,
        colour: palette.colour,
        accent: palette.accent,
        kicker: copy.slide_waiting || 'Your preview',
        status: this.previewErrorCopy(s),
        support: copy.quota_edits || '',
        skipLabel: copy.skip || '',
        showSkip: true,
      });
      this.renderPrintPreviewChrome();
      return;
    }

    var blocked = this.previewCanOffer() && !this.previewAllowsNewArtwork() && !frameOnly && !this.previewIsBusy();
    if (blocked && !(this._previewRoomReady && s && s.room_url)) {
      var limitStatus = !this.previewPoolAvailable()
        ? this.previewErrorCopy({ error: 'preview_daily_limit', errorMessage: (this.previewQuota && this.previewQuota.message) || '' })
        : this.previewErrorCopy({ error: 'preview_owner_limit', preview_quota: this.previewQuota });
      gallery.upsertGeneratedSlides({
        mode: 'error',
        activate: false,
        motion: 'off',
        photoUrl: photoUrl,
        photoUrls: photoUrls,
        colour: palette.colour,
        accent: palette.accent,
        kicker: copy.slide_waiting || 'Your preview',
        status: limitStatus,
        support: copy.quota_edits || '',
        skipLabel: copy.skip || '',
        showSkip: true,
      });
      this.renderPrintPreviewChrome();
      return;
    }

    var statusKey = checking ? 'screening' : (this._previewDecoding ? 'decoding' : ((s && s.status) || 'queued'));
    var busyStatus = statusKey === 'queued' || statusKey === 'generating' || statusKey === 'rendering' || statusKey === 'decoding';
    if (busyStatus && !this._previewResume) this._previewMotionStarted = true;
    if ((statusKey === 'generating' || statusKey === 'rendering') && this._previewResume) this._previewMotionStarted = true;
    var motionOn = this._previewMotionStarted && busyStatus && statusKey !== 'screening';
    if (this.previewCanOffer() || checking) {
      var first = !this._previewActivated;
      gallery.upsertGeneratedSlides({
        mode: 'waiting',
        activate: first || !!opts.activate,
        motion: motionOn ? 'on' : 'off',
        photoUrl: photoUrl,
        photoUrls: photoUrls,
        colour: palette.colour,
        accent: palette.accent,
        kicker: (statusKey === 'ready' || statusKey === 'decoding')
          ? (copy.ready || copy.slide_waiting)
          : (copy.slide_waiting || 'Your preview'),
        status: this._previewRoomFailed
          ? (copy.image_error || copy.error || '')
          : this.previewStatusCopy(statusKey, s && s.slow, frameOnly),
        support: (statusKey === 'screening' || this._previewRoomFailed) ? '' : support,
        skipLabel: copy.skip || '',
        showSkip: !!(s && (s.slow || s.status === 'error')) || !!this._previewRoomFailed,
        retryLabel: copy.close_retry || '',
        showRetry: !!this._previewRoomFailed,
      });
      if (first) this._previewActivated = true;
    }
    this.renderPrintPreviewChrome();
  };

  Controller.prototype.decodePreviewImage = function (url) {
    return new Promise(function (resolve, reject) {
      if (!url) { reject(new Error('missing')); return; }
      var img = new Image();
      var settled = false;
      var done = function () {
        if (settled) return;
        settled = true;
        if (typeof img.decode === 'function') {
          img.decode().then(function () { resolve(img); }).catch(function () { resolve(img); });
        } else {
          resolve(img);
        }
      };
      img.onload = done;
      img.onerror = function () { reject(new Error('decode')); };
      img.src = url;
      if (img.complete && img.naturalWidth) done();
    });
  };

  Controller.prototype.decodePreviewImages = function (roomUrl, closeUrl) {
    return Promise.all([this.decodePreviewImage(roomUrl), this.decodePreviewImage(closeUrl)]);
  };

  Controller.prototype.refreshPreviewUrls = function () {
    var preview = this.printPreview;
    if (!preview || typeof preview.refreshUrls !== 'function') {
      return Promise.resolve(preview && preview.state);
    }
    return preview.refreshUrls().catch(function () { return preview.state; });
  };

  Controller.prototype.decodePreviewImageWithRetry = function (kind, epoch) {
    var self = this;
    var attempts = 0;
    function urlNow() {
      var s = self.printPreview && self.printPreview.state;
      if (!s) return '';
      return kind === 'close' ? s.close_url : s.room_url;
    }
    function once() {
      if (epoch !== self._previewRevealEpoch) return Promise.reject(new Error('stale'));
      return self.decodePreviewImage(urlNow()).catch(function (err) {
        attempts += 1;
        if (epoch !== self._previewRevealEpoch) return Promise.reject(new Error('stale'));
        if (attempts >= 3) return Promise.reject(err);
        return self.refreshPreviewUrls().then(function () {
          return new Promise(function (resolve) { setTimeout(resolve, Math.min(400 * attempts, 1600)); });
        }).then(once);
      });
    }
    return once();
  };

  Controller.prototype.previewJobIsCurrent = function (epoch, key, previewId) {
    if (epoch !== this._previewRevealEpoch) return false;
    if (!this.printPreview || this.printPreview.state.status !== 'ready') return false;
    if (this._previewCartFrozen) return false;
    if (key && this.printPreview.key && this.printPreview.key !== key) return false;
    if (previewId && this.printPreview.state.preview_id !== previewId) return false;
    var now = this.previewArtworkKey(this.previewInput());
    if (this.printPreview.key && this.printPreview.key !== now) return false;
    return true;
  };

  Controller.prototype.flushPreviewPending = function () {
    if (!this.printPreview || this.previewIsBusy() || this._previewStarting) return false;
    if (this._previewPending) {
      var pending = this._previewPending;
      this._previewPending = null;
      this._previewPendingFrame = false;
      if (!this.previewAllowsNewArtwork()) {
        this.invalidatePreviewAttachment();
        if (this.printPreview.state && this.printPreview.state.room_url) this._previewRoomReady = true;
        this.renderPrintPreviewGallery();
        this.renderPreviewOffer();
        return true;
      }
      this._previewRoomReady = false;
      this._previewCloseReady = false;
      this._previewDecoding = false;
      this._previewMotionStarted = false;
      this._previewResume = false;
      this._previewRevealEpoch += 1;
      this.printPreview.configure(pending, this.previewImageKeys());
      this.startPrintPreview({ artworkChanged: true });
      return true;
    }
    if (this._previewPendingFrame) {
      this._previewPendingFrame = false;
      this.printPreview.configure(this.previewInput(), this.previewImageKeys());
      this.startPrintPreview({ frameOnly: true });
      return true;
    }
    return false;
  };

  Controller.prototype.finishRoomReveal = function (epoch, key, previewId) {
    if (!this.previewJobIsCurrent(epoch, key, previewId)) return;
    if (this.flushPreviewPending()) return;
    this._previewDecoding = false;
    this._previewRoomReady = true;
    this._previewRevealedId = previewId;
    var immediate = !this._previewMotionStarted;
    this._previewRevealing = !immediate;
    var matches = this.printPreview.key === this.previewArtworkKey(this.previewInput());
    if (matches && !this.printPreview.state.selected) {
      try { this.printPreview.select(); } catch (_) { /* not ready */ }
    } else if (!matches) {
      this.invalidatePreviewAttachment();
    }
    this.renderPrintPreviewGallery({ activate: false, reveal: this._previewRevealing, immediate: immediate });
    this.loadPreviewClose(epoch, key, previewId);
    if (this._previewRevealing) {
      var self = this;
      setTimeout(function () {
        if (self._previewRevealEpoch === epoch) self._previewRevealing = false;
      }, 850);
    }
  };

  Controller.prototype.loadPreviewClose = function (epoch, key, previewId) {
    var self = this;
    var s = this.printPreview && this.printPreview.state;
    if (!s || !s.close_url) {
      this._previewCloseFailed = true;
      this.renderPrintPreviewGallery({ activate: false, immediate: true });
      return;
    }
    this.decodePreviewImageWithRetry('close', epoch).then(function () {
      if (!self.previewJobIsCurrent(epoch, key, previewId)) return;
      self._previewCloseReady = true;
      self._previewCloseFailed = false;
      self.renderPrintPreviewGallery({ activate: false, immediate: true });
    }).catch(function (err) {
      if (err && err.message === 'stale') return;
      if (!self.previewJobIsCurrent(epoch, key, previewId)) return;
      self._previewCloseReady = false;
      self._previewCloseFailed = true;
      self.renderPrintPreviewGallery({ activate: false, immediate: true });
    });
  };

  Controller.prototype.retryPreviewRoom = function () {
    if (!this.printPreview || !this.printPreview.state || this.printPreview.state.status !== 'ready') return;
    this._previewRoomFailed = false;
    this._previewRoomReady = false;
    this._previewDecoding = true;
    this.renderPrintPreviewGallery();
    var epoch = ++this._previewRevealEpoch;
    var key = this.printPreview.key;
    var previewId = this.printPreview.state.preview_id;
    var self = this;
    this.decodePreviewImageWithRetry('room', epoch).then(function () {
      self.finishRoomReveal(epoch, key, previewId);
    }).catch(function (err) {
      if (err && err.message === 'stale') return;
      self._previewDecoding = false;
      self._previewRoomFailed = true;
      self.renderPrintPreviewGallery();
    });
  };

  Controller.prototype.retryPreviewClose = function () {
    if (!this.printPreview || !this.printPreview.state || this.printPreview.state.status !== 'ready') return;
    this._previewCloseFailed = false;
    this.loadPreviewClose(this._previewRevealEpoch, this.printPreview.key, this.printPreview.state.preview_id);
  };

  Controller.prototype.onPrintPreviewChange = function () {
    var self = this;
    var s = this.printPreview && this.printPreview.state;
    if (!s) return;
    if (s.preview_quota) this.applyPreviewQuota(s.preview_quota, s._sentAt);
    if (s.error === 'preview_disabled') {
      this.previewEnabled = false;
      this.teardownPrintPreview();
      return;
    }
    if (s.status === 'skipped') {
      this._previewPending = null;
      this._previewPendingFrame = false;
      this.renderPrintPreviewGallery();
      return;
    }
    if (s.status === 'ready' && s.room_url) {
      if (this._previewCartFrozen) {
        this.renderPrintPreviewGallery();
        return;
      }
      if (this.flushPreviewPending()) return;
      var currentKey = this.previewArtworkKey(this.previewInput());
      if (this.printPreview.key && this.printPreview.key !== currentKey) {
        this.renderPrintPreviewGallery();
        return;
      }
      if (this._previewRoomReady && this._previewRevealedId === s.preview_id) {
        if (this._previewRevealing) return;
        this.renderPrintPreviewGallery({ activate: false, immediate: true });
        if (!this._previewCloseReady && s.close_url && !this._previewCloseFailed) {
          this.loadPreviewClose(this._previewRevealEpoch, this.printPreview.key, s.preview_id);
        }
        return;
      }
      this._previewDecoding = true;
      this.renderPrintPreviewGallery();
      var epoch = this._previewRevealEpoch;
      var key = this.printPreview.key;
      var previewId = s.preview_id;
      this.decodePreviewImageWithRetry('room', epoch).then(function () {
        self.finishRoomReveal(epoch, key, previewId);
      }).catch(function (err) {
        if (err && err.message === 'stale') return;
        if (!self.previewJobIsCurrent(epoch, key, previewId)) return;
        self._previewDecoding = false;
        self._previewRoomFailed = true;
        self.renderPrintPreviewGallery();
      });
      return;
    }
    this.renderPrintPreviewGallery({ frameOnly: this._previewFrameOnly });
  };

  Controller.prototype.startPrintPreview = function (opts) {
    opts = opts || {};
    var self = this;
    if (!this.printPreview || this._previewStarting || this._previewCartFrozen) return;
    if (this.previewIsBusy()) return;
    if (!opts.frameOnly && !this.previewAllowsNewArtwork(opts)) {
      this.invalidatePreviewAttachment();
      this.renderPrintPreviewGallery();
      this.renderPreviewOffer();
      return;
    }
    this._previewResume = !opts.frameOnly && !!this.printPreview.state.preview_id;
    if (!opts.frameOnly) {
      this._previewRoomReady = false;
      this._previewCloseReady = false;
      this._previewDecoding = false;
      this._previewCloseFailed = false;
      this._previewRoomFailed = false;
      this._previewRevealEpoch += 1;
      if (!this._previewResume) this._previewMotionStarted = false;
    }
    this._previewStarting = true;
    this._previewFrameOnly = !!opts.frameOnly;
    this.printPreview.start({ keepImages: !!opts.frameOnly }).catch(function () { /* view handles error */ }).then(function () {
      self._previewStarting = false;
      self._previewFrameOnly = false;
      if (self._previewCartFrozen) return;
      self.flushPreviewPending();
    });
  };

  Controller.prototype.syncPrintPreview = function (opts) {
    opts = opts || {};
    if (!this.el) return;
    if (this._previewCartFrozen) return;
    if (!this.previewEnabled) {
      this.teardownPrintPreview();
      return;
    }
    if (!this.previewCanOffer()) {
      if (this.previewIsChecking()) {
        this.renderPrintPreviewGallery({ activate: !this._previewActivated });
        return;
      }
      if (this.printPreview && this.printPreview.state && this.printPreview.state.status === 'skipped') {
        this.renderPrintPreviewChrome();
        return;
      }
      this.teardownPrintPreview();
      return;
    }
    if (!this.ensurePrintPreview()) return;
    var input = this.previewInput();
    var key = this.previewArtworkKey(input);
    var prevVariant = this._previewVariantId;
    var variantChanged = !!prevVariant && prevVariant !== input.variant_id;
    this._previewVariantId = input.variant_id;
    var busy = this.previewIsBusy() || this._previewStarting;
    var keyChanged = key !== this._previewLastKey && key !== this.printPreview.key;

    if (busy) {
      if (key !== this.printPreview.key) this._previewPending = input;
      else if (variantChanged) this._previewPendingFrame = true;
      this.renderPrintPreviewGallery();
      return;
    }

    if (keyChanged && !this.previewAllowsNewArtwork()) {
      this.invalidatePreviewAttachment();
      this.renderPrintPreviewGallery();
      this.renderPreviewOffer();
      return;
    }

    this.printPreview.configure(input, this.previewImageKeys());
    this._previewLastKey = key;

    if (variantChanged && !keyChanged && this.printPreview.state && this.printPreview.state.preview_id) {
      this.startPrintPreview({ frameOnly: true });
      this.renderPrintPreviewGallery({ frameOnly: true });
      return;
    }

    var s = this.printPreview.state || {};
    if (s.status === 'error' && !keyChanged && s.preview_id) {
      this.renderPrintPreviewGallery();
      return;
    }

    if (s.status === 'idle' || !s.preview_id || keyChanged) {
      if (!this.previewAllowsNewArtwork({ frameOnly: false })) {
        this.invalidatePreviewAttachment();
        this.renderPrintPreviewGallery();
        this.renderPreviewOffer();
        return;
      }
      this.startPrintPreview({ artworkChanged: true });
    }
    this.renderPrintPreviewGallery({ activate: !this._previewActivated });
  };

  function init(sectionEl) {
    if (sectionEl._fabPortraitInited) return sectionEl._fabPortrait;
    sectionEl._fabPortraitInited = true;
    sectionEl._fabPortrait = new Controller(sectionEl);
    return sectionEl._fabPortrait;
  }

  function bootAll() {
    document.querySelectorAll('[data-fab-portrait-section]').forEach(init);
  }

  window.FabPortraitController = { init: init, bootAll: bootAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }
})();
